import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import {
  additionalUnitLabels,
  BasicDisplayUnits,
  getPressurePumpUnitLabel,
  UnitBase,
  unitLabels
} from 'hooks/useDisplayUnits/useDisplayUnits';
import ProjectContext from 'modules/project/providers/project/context';
import {
  CustomUnitFields,
  DisplayUnits,
  FlushResultDto,
  FlushSpecificationDto,
  FlushStageType,
  MetricPressureUnit,
  UnitSystem
} from 'providers/api';
import React from 'react';
import { createProjectNumberFormatter } from 'utils';
import { pumpPressureBarToKpa } from 'utils/unitConversions';
import { flushStageTypeLabel } from '../../MineModel/Flushing/flushLabels';
import { airPushesWaterToStope, plannedWaterMinutes, waterAloneReachesStope } from '../../MineModel/Flushing/flushOutcome';
import { fromFlushResultDto, toFlushSpec } from '../../MineModel/Flushing/flushRecipeMapping';
import { formatMinutes } from './FlushForm/flushFormHelpers';
import FlushScrub, { FlushScrubContext } from './FlushScrub';
import { unitSystemRawValue, UnitSystemValue } from './UnitSystemDetail';

const isFiniteNumber = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value);

type FlushStage = FlushResultDto['stages'][number];

const VOLUME_DECIMAL_PLACES = 1;
const DISTANCE_DECIMAL_PLACES = 0;
const FLOW_RATE_DECIMAL_PLACES = 0;

const PREFIX = 'FlushDetails';

const classes = {
  table: `${PREFIX}-table`,
};

const Root = styled('div')(() => ({
  [`& .${classes.table}`]: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      fontSize: '0.9em',
      padding: 0,
      margin: 0,
      paddingBottom: 8,
    },
  },
}));

interface FlushDetailsProps {
  specification: FlushSpecificationDto;
  result: FlushResultDto;
  linkedRecipeReference?: string;
  unitSystemPreference: UnitSystem;
  customUnitFields: CustomUnitFields[];
  // When given, the details end with a scrub control that drives the HGL overlay.
  scrub?: FlushScrubContext;
}

const FlushDetails = ({ specification, result, linkedRecipeReference, unitSystemPreference, customUnitFields, scrub }: FlushDetailsProps) => {
  const projectContext = React.useContext(ProjectContext);
  const scrubSpec = React.useMemo(() => toFlushSpec(specification), [specification]);
  const scrubResult = React.useMemo(() => fromFlushResultDto(result), [result]);
  const metricPressureUnit = projectContext?.state.metricPressureUnit ?? MetricPressureUnit.Bar;
  const isKiloPascal = metricPressureUnit === MetricPressureUnit.KiloPascal && unitSystemPreference === UnitSystem.Metric;
  const pressureUnitInfo = getPressurePumpUnitLabel(metricPressureUnit);
  const pressureDecimalPlaces = isKiloPascal ? 0 : 1;
  const toDisplayPressure = (bar: number) => (isKiloPascal ? pumpPressureBarToKpa(bar) : bar);
  const volumeUnitInfo = unitLabels[BasicDisplayUnits.Volume];
  const distanceUnitInfo = unitLabels[BasicDisplayUnits.Distance];
  const flowRateUnitInfo = additionalUnitLabels[DisplayUnits.FlowRate];

  const formatNumber = createProjectNumberFormatter(projectContext?.state.decimalSeparator, projectContext?.state.thousandsSeparator);
  const unitValue = (value: number, unitInfo: UnitBase, decimalPlaces: number) => (
    <UnitSystemValue value={value} unitInfo={unitInfo} unitSystem={unitSystemPreference} decimalPlaces={decimalPlaces} customUnitFields={customUnitFields} />
  );
  const volume = (value: number) => unitValue(value, volumeUnitInfo, VOLUME_DECIMAL_PLACES);
  const distance = (value: number) => unitValue(value, distanceUnitInfo, DISTANCE_DECIMAL_PLACES);
  const pressure = (bar: number) => unitValue(toDisplayPressure(bar), pressureUnitInfo, pressureDecimalPlaces);
  const flowRate = (value: number) => unitValue(value, flowRateUnitInfo, FLOW_RATE_DECIMAL_PLACES);
  // "0.0 → 3.9 m³": the unit once, at the end of the range, so a stage cell stays narrow.
  const range = (from: number, to: number, unitInfo: UnitBase, decimalPlaces: number) => (
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      {formatNumber(unitSystemRawValue({ value: from, unitInfo, unitSystem: unitSystemPreference, decimalPlaces, customUnitFields }), decimalPlaces)}
      {' → '}
      {unitValue(to, unitInfo, decimalPlaces)}
    </Box>
  );

  const hasAirStart = isFiniteNumber(result.airStartWaterVolume) && isFiniteNumber(result.airStartDistance) && isFiniteNumber(result.airStartTimeMinutes);
  const reachesStope = waterAloneReachesStope(result);
  const stageSuffix = (stageType: FlushStageType | null | undefined) => (stageType !== undefined && stageType !== null ? ` - ${flushStageTypeLabel(stageType)}` : '');

  const handoverRow: React.ReactNode = result.flushPumpCanCompleteAlone
    ? i18n._('From the start')
    : (
      <>
        {i18n._('Water front at')}
        {' '}
        {distance(result.pastePumpHandoverDistance)}
      </>
    );

  const airStartRow: React.ReactNode = hasAirStart
    ? (
      <>
        {i18n._('After')}
        {' '}
        {volume(result.airStartWaterVolume as number)}
        {' ('}
        {distance(result.airStartDistance as number)}
        {'), '}
        {formatMinutes(result.airStartTimeMinutes as number)}
        {stageSuffix(result.airStartStage)}
      </>
    )
    : i18n._('Not reached within the planned flush');

  const waterAtStopeRow: React.ReactNode = (() => {
    if (reachesStope) return `${formatMinutes(result.timeToWaterAtStopeMinutes as number)}${stageSuffix(result.waterAtStopeStage)}`;
    if (airPushesWaterToStope(result)) return `${formatMinutes(plannedWaterMinutes(result))} ${i18n._('of water, then the air purge')}`;
    return i18n._('Not reached within the planned flush');
  })();

  const rows: [string, React.ReactNode][] = [
    [i18n._('Line volume'), volume(result.totalLineVolume)],
    [i18n._('Line length'), distance(result.totalLineLength)],
    [i18n._('Planned flush volume'), volume(result.finalFlushVolume)],
    [i18n._('Water through the paste pump'), result.flushPumpCanCompleteAlone ? i18n._('None') : volume(result.pastePumpWaterVolume)],
    [i18n._('Flush pump takes over'), handoverRow],
    ...(specification.airFlushEnabled ? [[i18n._('Air purge can start'), airStartRow] as [string, React.ReactNode]] : []),
    [i18n._('Water at the stope'), waterAtStopeRow],
    [i18n._('Max required pressure'), pressure(result.maxRequiredPressure)],
  ];

  // One column per stage: the panel is narrow, so the stages run across and the quantities down.
  const stageKey = (stage: FlushStage) => `${stage.stageType}-${stage.startVolume}`;
  const stageRows: [string, (stage: FlushStage) => React.ReactNode][] = [
    [i18n._('Water'), (stage) => range(stage.startVolume, stage.endVolume, volumeUnitInfo, VOLUME_DECIMAL_PLACES)],
    [i18n._('Front'), (stage) => range(stage.startDistance, stage.endDistance, distanceUnitInfo, DISTANCE_DECIMAL_PLACES)],
    [i18n._('Flow Rate'), (stage) => flowRate(stage.flowRate)],
    [
      i18n._('Pressure'),
      (stage) => range(toDisplayPressure(stage.requiredPressureStart), toDisplayPressure(stage.requiredPressureEnd), pressureUnitInfo, pressureDecimalPlaces),
    ],
    [i18n._('Duration'), (stage) => formatMinutes(stage.durationMinutes)],
  ];

  return (
    <Root>
      {linkedRecipeReference && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption">
            <Trans id="Displaces recipe:">Displaces recipe: </Trans>
            {linkedRecipeReference}
          </Typography>
        </Box>
      )}
      <Table className={classes.table} aria-label={i18n._('Flush summary table')} size="small">
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell component="th" scope="row">{label}</TableCell>
              <TableCell align="right">{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Divider />
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          <Trans id="Stages">Stages</Trans>
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table className={classes.table} aria-label={i18n._('Flush stages table')} size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              {result.stages.map((stage) => (
                <TableCell key={stageKey(stage)} align="right">{flushStageTypeLabel(stage.stageType)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {stageRows.map(([label, cell]) => (
              <TableRow key={label}>
                <TableCell component="th" scope="row">{label}</TableCell>
                {result.stages.map((stage) => (
                  <TableCell key={stageKey(stage)} align="right">{cell(stage)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {scrub && (
        <FlushScrub
          scrubKey={scrub.scrubKey}
          route={scrub.route}
          paste={scrub.paste}
          spec={scrubSpec}
          result={scrubResult}
          unitSystemPreference={unitSystemPreference}
          customUnitFields={customUnitFields}
        />
      )}
      {result.warnings.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {result.warnings.map((warning) => (
            <Alert key={warning.code} severity="warning">{warning.description}</Alert>
          ))}
        </Box>
      )}
    </Root>
  );
};

export default FlushDetails;
